/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as ActionTypes from "constants/ActionTypes";
import { ClipId, isClipWithAsset } from "types/Clip";
import { AppAction } from "types/AppAction";
import {
  setAssetTranscriptionToProcessing,
  transcribeAssetFailed,
  transcribeAssetSuccess,
} from "actions/AssetActions";
import { addToast } from "actions/ToastMessageActions";
import TranscriptionState from "constants/TranscriptionState";
import {
  startAssetTranscription,
  startRecordingSessionTranscription,
} from "utilities/transcription/TranscriptionUtilities";
import {
  createTrackParticipant,
  updateTrackParticipant,
} from "utilities/ProjectUtilities";
import { AssetId } from "types/Asset";

interface TranscribeClipProps {
  clipId: ClipId;
}

export function transcribeClip({ clipId }: TranscribeClipProps): AppAction {
  return async (dispatch, getState): Promise<void> => {
    const { project, clips, assets, recordingSessions } = getState();
    const clip = clips.find((clip) => clip.id === clipId);
    if (!clip || !isClipWithAsset(clip)) {
      return;
    }

    const assetId = clip.assetId;
    const asset = assets[assetId];
    if (asset?.transcriptionState === TranscriptionState.COMPLETE) {
      dispatch(transcribeAssetSuccess(assetId));
      return;
    }

    try {
      dispatch(setAssetTranscriptionToProcessing({ clipId }));

      // Note: This won't match if the clip has been changed and the no longer matches!
      const recordingSession = Object.values(recordingSessions).find(
        (recordingSession) => recordingSession.clipId === clipId
      );

      if (recordingSession) {
        await startRecordingSessionTranscription({
          project,
          recordingSessionId: recordingSession.id,
        });
      } else {
        await startAssetTranscription({ project, assetId });
      }
    } catch (error) {
      console.error("Error transcribing asset: ", error);
      newrelic.noticeError(error);
      dispatch(transcribeAssetFailed(assetId));
      dispatch(addToast({ message: I18n.t("transcription.error") }));
    }
  };
}

interface UpdateSpeakerDisplayNameForAssetProps {
  assetId: AssetId;
  unidentifiedSpeakerNumber: number;
  displayName: string;
}
export function updateSpeakerDisplayNameForAsset({
  assetId,
  unidentifiedSpeakerNumber,
  displayName,
}: UpdateSpeakerDisplayNameForAssetProps): AppAction {
  return async (dispatch, getState): Promise<void> => {
    const { project, assets } = getState();
    const asset = assets[assetId];

    if (!asset.transcript) {
      throw new Error("Asset transcript is missing for clip");
    }

    const existingSpeakerSegment = asset.transcript.find(
      (segment) =>
        segment.unidentifiedSpeakerNumber === unidentifiedSpeakerNumber
    );
    if (!existingSpeakerSegment) {
      throw new Error("Speaker segment not found in transcript");
    }

    const data = {
      adobeId: existingSpeakerSegment.adobeId,
      displayName,
      unidentifiedSpeakerNumber,
      isAnonymous: existingSpeakerSegment.isAnonymous,
    };

    let trackParticipantId = "";
    try {
      // Update the existing track_participant if there is one.
      const existingParticipant =
        asset.participants?.[unidentifiedSpeakerNumber];
      if (existingParticipant) {
        trackParticipantId = await updateTrackParticipant({
          projectId: project.id,
          assetId,
          trackParticipantId: existingParticipant.id,
          data,
        });
      } else {
        trackParticipantId = await createTrackParticipant({
          projectId: project.id,
          assetId,
          data,
        });
      }

      dispatch({
        type: ActionTypes.UPDATE_SPEAKER_DISPLAY_NAME,
        ...data,
        assetId,
        trackParticipantId,
      });
    } catch (error) {
      newrelic.noticeError(error, {
        ...data,
        trackParticipantId,
        adobeId: data.adobeId || "",
        isAnonymous: data.isAnonymous?.toString() || "false",
      });
      dispatch(
        addToast({ message: I18n.t("avatar_actions.edit_name.failed") })
      );
    }
  };
}
